import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { filter, finalize, map, of, OperatorFunction, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';

import {
  LoadPpfpProcurementPackagesFailure,
  LoadPpfpProcurementPackagesSuccess,
  LoadPpfpsFailure,
  LoadPpfpsSuccess,
  LoadPpfpTasksFailure,
  LoadPpfpTasksSuccess,
  LoadPpfpTaskTypesFailure,
  LoadPpfpTaskTypesSuccess,
  LoadPpfpTaskGroupsFailure,
  LoadPpfpTaskGroupsSuccess,
  PpfpActions,
  ChangeProject,
  ProjectActionTypes,
  ProjectsLoadSuccess,
  ResourceReset,
} from '@completion/actions';
import { ResourceType } from '@completion/enums';
import { Ppfp, PpfpActionSummary, PpfpTask, PpfpTaskGroup, PpfpTaskType, ProcurementPackage, ResourceDescriptor } from '@completion/models';
import { State } from '@completion/reducers';
import {
  getPpfpProcurementPackagesRequestState,
  getPpfpsRequestState,
  getPpfpTasksRequestState,
  ppfpPackageActionsResource,
  getPpfpTimelineState,
} from '@completion/selectors';
import { NotificationService, PpfpService } from '@completion/services';
import { trackResource, TrackResourceOptions } from './resource-fetch.util';

type PpfpLoadAction = ReturnType<
  | typeof PpfpActions.loadTasks
  | typeof PpfpActions.loadPpfps
  | typeof PpfpActions.loadProcurementPackages
  | typeof PpfpActions.loadTaskTypes
  | typeof PpfpActions.loadTaskGroups
>;

@Injectable()
export class PpfpEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<State>,
    private readonly ppfpService: PpfpService,
    private readonly toastr: NotificationService,
  ) {}

  previewTaskSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.previewTaskSchedule),
      withLatestFrom(this.store.select(getPpfpTimelineState)),
      filter(
        ([action, state]) =>
          state.projectId === action.projectId &&
          state.context?.projectId === action.projectId &&
          state.context?.ppfpId === action.ppfpId &&
          state.tasks.some((task) => task.id === action.taskId && (action.ppfpId === null || task.ppfpId === action.ppfpId)),
      ),
      map(([action, state]) => {
        const task = { ...state.tasks.find((candidate) => candidate.id === action.taskId), ...state.previews[action.taskId] };
        const changes: typeof action.changes = {};
        for (const field of ['plannedDate', 'estimatedDate', 'actualDate'] as const) {
          if (action.changes[field] !== undefined && action.changes[field] !== task[field]) changes[field] = action.changes[field];
        }
        return PpfpActions.previewTaskScheduleAccepted({ ...action, changes });
      }),
      filter((action) => Object.keys(action.changes).length > 0),
      tap(({ taskId, changes }) => console.info('PPFP timeline preview (not saved)', { taskId, changes })),
    ),
  );

  loadPackageActions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.loadPackageActions),
      switchMap((action) => {
        const descriptor = ppfpPackageActionsResource(action);
        let settled = false;
        return of(action).pipe(
          trackResource<PpfpActionSummary[]>({
            actions$: this.actions$,
            store: this.store,
            toastr: this.toastr,
            service: this.ppfpService,
            method: 'getPackageActions',
            descriptor,
            serviceArgs: () => [action],
            onSuccess: (actions) => PpfpActions.loadPackageActionsSuccess({ ...action, actions }),
            onError: () => PpfpActions.loadPackageActionsFailure(action),
            errorMessage: () => 'Action points could not be loaded. Try again.',
          }),
          tap(() => {
            settled = true;
          }),
          takeUntil(
            this.actions$.pipe(
              ofType(PpfpActions.clearPackageActions, ProjectActionTypes.ChangeProject, ProjectActionTypes.ProjectsLoadSuccess),
            ),
          ),
          finalize(() => {
            if (!settled) this.store.dispatch(new ResourceReset(descriptor));
          }),
        );
      }),
    ),
  );

  ensureTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.ensureTasks),
      withLatestFrom(this.store.select(getPpfpTasksRequestState)),
      filter(([action, state]) => state.projectId !== action.projectId || (!state.loaded && !state.loading)),
      map(([action]) => PpfpActions.loadTasks({ projectId: action.projectId })),
    ),
  );

  loadTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.loadTasks),
      this.trackLatestResource<PpfpTask[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.ppfpService,
        method: 'getProcurementPackageTasks',
        descriptor: { type: ResourceType.GetProcurementPackageTasks },
        serviceArgs: (action) => [action.projectId],
        successAction: LoadPpfpTasksSuccess,
        errorAction: LoadPpfpTasksFailure,
        errorMessage: () => 'Failed to load PPFP Tasks',
      }),
    ),
  );

  ensurePpfps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.ensurePpfps),
      withLatestFrom(this.store.select(getPpfpsRequestState)),
      filter(([action, state]) => state.projectId !== action.projectId || (!state.loaded && !state.loading)),
      map(([action]) => PpfpActions.loadPpfps({ projectId: action.projectId })),
    ),
  );

  loadPpfps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.loadPpfps),
      this.trackLatestResource<Ppfp[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.ppfpService,
        method: 'getPpfps',
        descriptor: { type: ResourceType.GetPpfps },
        serviceArgs: (action) => [action.projectId],
        successAction: LoadPpfpsSuccess,
        errorAction: LoadPpfpsFailure,
        errorMessage: () => 'Failed to load PPFPs',
      }),
    ),
  );

  ensureProcurementPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.ensureProcurementPackages),
      withLatestFrom(this.store.select(getPpfpProcurementPackagesRequestState)),
      filter(([action, state]) => state.projectId !== action.projectId || (!state.loaded && !state.loading)),
      map(([action]) => PpfpActions.loadProcurementPackages({ projectId: action.projectId })),
    ),
  );

  loadProcurementPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.loadProcurementPackages),
      this.trackLatestResource<ProcurementPackage[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.ppfpService,
        method: 'getProcurementPackages',
        descriptor: { type: ResourceType.GetPpfpProcurementPackages },
        serviceArgs: (action) => [action.projectId],
        successAction: LoadPpfpProcurementPackagesSuccess,
        errorAction: LoadPpfpProcurementPackagesFailure,
        errorMessage: () => 'Failed to load PPFP Procurement Packages',
      }),
    ),
  );

  loadTaskTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.loadTaskTypes),
      this.trackLatestResource<PpfpTaskType[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.ppfpService,
        method: 'getTaskTypes',
        descriptor: { type: ResourceType.GetPpfpTaskTypes },
        serviceArgs: (action) => [action.projectId],
        successAction: LoadPpfpTaskTypesSuccess,
        errorAction: LoadPpfpTaskTypesFailure,
        errorMessage: () => 'Failed to load PPFP Task Types',
      }),
    ),
  );

  loadTaskGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PpfpActions.loadTaskGroups),
      this.trackLatestResource<PpfpTaskGroup[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.ppfpService,
        method: 'getTaskGroups',
        descriptor: { type: ResourceType.GetPpfpTaskGroups },
        serviceArgs: (action) => [action.projectId],
        successAction: LoadPpfpTaskGroupsSuccess,
        errorAction: LoadPpfpTaskGroupsFailure,
        errorMessage: () => 'Failed to load PPFP Task Groups',
      }),
    ),
  );

  private trackLatestResource<Resource>(
    options: TrackResourceOptions<Resource> & { descriptor: ResourceDescriptor },
  ): OperatorFunction<PpfpLoadAction, Action> {
    return switchMap((action) => {
      let settled = false;
      const cancelled = this.actions$.pipe(
        ofType<PpfpLoadAction | ChangeProject | ProjectsLoadSuccess | ReturnType<typeof PpfpActions.clearTasks>>(
          PpfpActions.loadTasks.type,
          PpfpActions.loadPpfps.type,
          PpfpActions.loadProcurementPackages.type,
          PpfpActions.loadTaskTypes.type,
          PpfpActions.loadTaskGroups.type,
          ProjectActionTypes.ChangeProject,
          ProjectActionTypes.ProjectsLoadSuccess,
          PpfpActions.clearTasks.type,
        ),
        filter((event) => {
          if (event.type === PpfpActions.clearTasks.type) {
            return options.descriptor.type === ResourceType.GetProcurementPackageTasks;
          }
          const projectId =
            event.type === ProjectActionTypes.ChangeProject
              ? event.project?.id
              : event.type === ProjectActionTypes.ProjectsLoadSuccess
                ? event.currentProject?.id
                : event.projectId;
          return projectId !== action.projectId;
        }),
      );

      return of(action).pipe(
        trackResource<Resource>(options),
        tap(() => {
          settled = true;
        }),
        takeUntil(cancelled),
        finalize(() => {
          if (!settled) this.store.dispatch(new ResourceReset(options.descriptor));
        }),
      );
    });
  }
}
